import { reportInteraction } from '@grafana/runtime';

const queryLibraryInteraction = (event: string, properties?: Record<string, unknown>) => {
  reportInteraction(`query_library-${event}`, properties);
};

export interface QueryLibraryEventsPropertyMap {
  [key: string]: string | number | boolean | undefined;
}

export const QueryLibraryInteractions: Record<string, (properties?: QueryLibraryEventsPropertyMap) => void> = {
  queryLibraryOpened: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('opened', properties);
  },
  queryLibraryClosedWithoutSelection: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('closed_without_selection', properties);
  },
  queryLibraryClosedWithoutSavingNewQuery: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('closed_without_saving_new_query', properties);
  },
  // Outside Interactions
  savedQueriesDropdownOpened: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('saved_queries_dropdown_opened', properties);
  },
  addQueryFromLibraryClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('add_query_from_library_clicked', properties);
  },
  replaceWithQueryFromLibraryClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('replace_with_query_from_library_clicked', properties);
  },
  saveQueryToLibraryClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('save_query_to_library_clicked', properties);
  },
  // Creating new query
  cancelSaveNewQueryClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('cancel_save_new_query_clicked', properties);
  },
  // "New query" on the Saved Queries page → compose a brand-new query in Explore
  newSavedQueryFromExploreClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('new_saved_query_from_explore_clicked', properties);
  },
  /** Discarded the compose flow from the Explore banner, before the save drawer was ever opened. */
  newSavedQueryFromExploreCancelled: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('new_saved_query_from_explore_cancelled', properties);
  },
  /** The "Adding a new saved query" banner mounted in Explore. */
  newSavedQueryBannerShown: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('new_saved_query_banner_shown', properties);
  },
  saveQuerySuccess: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('save_query_success', properties);
  },
  editInExploreClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('edit_in_explore_clicked', properties);
  },
  updateQueryFromExploreCompleted: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('update_query_from_explore_completed', properties);
  },
  updateQueryFromExploreCancelled: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('update_query_from_explore_cancelled', properties);
  },

  // Inside Interactions
  // Filter
  searchBarFocused: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('search_bar_focused', properties);
  },
  dataSourceFilterChanged: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('data_source_filter_changed', properties);
  },
  userFilterChanged: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('user_filter_changed', properties);
  },
  sortingOptionChanged: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('sorting_option_changed', properties);
  },
  tagFilterChanged: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('tag_filter_changed', properties);
  },
  starredFilterChanged: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('starred_filter_changed', properties);
  },
  rememberFiltersToggled: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('remember_filters_toggled', properties);
  },
  // Bottom Action Row
  selectQueryClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('select_query_clicked', properties);
  },
  deleteQueryClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('delete_query_clicked', properties);
  },
  duplicateQueryClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('duplicate_query_clicked', properties);
  },
  saveRecentQueryClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('save_recent_query_clicked', properties);
  },
  openInDrilldownClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('open_in_drilldown_clicked', properties);
  },
  // Editing
  editQueryClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('edit_query_clicked', properties);
  },
  cancelEditClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('cancel_edit_clicked', properties);
  },
  saveEditClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('save_edit_clicked', properties);
  },
  queryLibraryClosedToEditQueryInExplore: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('query_library_closed_to_edit_query_in_explore', properties);
  },
  queryLibraryClosedToOpenQueryInExplore: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('query_library_closed_to_open_query_in_explore', properties);
  },
  tabClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('tab_clicked', properties);
  },
  // Favoriting
  favoriteQueryClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('favorite_query_clicked', properties);
  },
  // Template variable adjuster
  variableAdjusterCustomSelected: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('variable_adjuster_custom_selected', properties);
  },
  variableAdjusterTemplateVariableSelected: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('variable_adjuster_template_variable_selected', properties);
  },
  variableAdjusterCleared: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('variable_adjuster_cleared', properties);
  },

  // Saved Queries page. The `app` property (saved-queries-page / saved-queries-detail-page)
  // distinguishes the list page from the detail page, so these are not duplicated per surface.
  savedQueriesPageViewed: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('saved_queries_page_viewed', properties);
  },
  savedQueryDetailPageViewed: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('saved_query_detail_page_viewed', properties);
  },
  savedQueryRowClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('saved_query_row_clicked', properties);
  },
  openInExploreClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('open_in_explore_clicked', properties);
  },
  shareQueryClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('share_query_clicked', properties);
  },
  shareLinkCopied: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('share_link_copied', properties);
  },
  shareQueryTextCopied: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('share_query_text_copied', properties);
  },
  /** Kept separate from deleteQueryClicked so single-delete counts aren't inflated by bulk deletes. */
  bulkDeleteQueriesClicked: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('bulk_delete_queries_clicked', properties);
  },
  /**
   * The page's edit drawer closed without saving. A successful save closes via onSave, which bypasses
   * the drawer's guarded close, so this only fires on abandonment. `isNew` separates editing an
   * existing query from composing a brand-new one; duplicates get their own event below.
   */
  editDrawerClosedWithoutSaving: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('edit_drawer_closed_without_saving', properties);
  },
  /** Same drawer, but opened to duplicate an existing query — kept separate to avoid conflating the two. */
  duplicateDrawerClosedWithoutSaving: (properties?: QueryLibraryEventsPropertyMap) => {
    queryLibraryInteraction('duplicate_drawer_closed_without_saving', properties);
  },
};

export const dirtyFieldsToAnalyticsObject = (
  dirtyFields: Partial<{
    title?: boolean;
    description?: boolean;
    isVisible?: boolean;
    tags?: boolean[];
  }>
) => {
  const result: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(dirtyFields)) {
    if (typeof value === 'boolean') {
      result[key] = value;
    }
  }
  return result;
};
